// src/contracts/container_rental/schemas.ts
//
// Zod schemas for the Container Rental Manager forms. The cover/content
// snapshot fields are accepted as opaque payloads (z.any()) because the
// originating estimate already validated them via the Yup resolver in
// contracts/estimates/types.ts. We only validate the rental-specific fields.

import { z } from "zod";

export const rentalStatusSchema = z.enum([
  "draft",
  "active",
  "paused",
  "closed",
  "cancelled",
]);

export const roundStatusSchema = z.enum([
  "scheduled",
  "issued",
  "skipped",
  "cancelled",
]);

const positiveInt = z.number().int().positive();
const nonNegativeInt = z.number().int().nonnegative();
const nonEmptyDate = z.string().min(1, "날짜를 입력해 주세요.");

// clientName/siteName accept either the top-level field OR the matching cover.* snapshot
// field — backend extracts the index column from cover when the explicit field is empty,
// so we mirror that fallback here. The form auto-syncs cover.* ↔ top-level columns so the
// user only edits in one place.
export const containerRentalFormSchema = z
  .object({
    id: z.string().optional(),

    estimateId: z.string().nullable().optional(),

    // Snapshot of the source estimate — already validated upstream.
    cover: z.any(),
    content: z.any(),

    // Denormalized index columns (filled either directly or via cover sync).
    clientName: z.string().default(""),
    siteName: z.string().default(""),
    siteAddress: z.string().default(""),
    contactPerson: z.string().default(""),
    contactPhone: z.string().default(""),

    // Rental terms. containerModel/containerCount/monthlyRent are derived
    // server-side from content.items at save time, so the form does not
    // require them as inputs anymore — they're tolerated read-only summaries.
    containerModel: z.string().default(""),
    containerCount: nonNegativeInt.default(0),
    monthlyRent: nonNegativeInt.default(0),
    transportFee: nonNegativeInt,
    initialMonths: positiveInt,
    totalRounds: positiveInt,

    // Key dates — non-empty ISO strings.
    issueDate: nonEmptyDate,
    rentalStartDate: nonEmptyDate,

    status: rentalStatusSchema,

    memo: z.string().max(2000, "메모는 2000자를 넘을 수 없습니다.").default(""),
  })
  .superRefine((val, ctx) => {
    const cover = (val.cover ?? {}) as { companyName?: string; siteName?: string };
    if (!val.clientName?.trim() && !cover.companyName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["clientName"],
        message: "고객사를 입력해 주세요.",
      });
    }
    if (!val.siteName?.trim() && !cover.siteName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["siteName"],
        message: "현장명을 입력해 주세요.",
      });
    }
  });

export type ContainerRentalFormValues = z.infer<typeof containerRentalFormSchema>;

// ---------- round / lifecycle action payloads ----------

export const issueRoundSchema = z.object({
  issuedAt: nonEmptyDate,
  invoiceNumber: z.string().min(1, "세금계산서 번호를 입력해 주세요."),
  issuedMemo: z.string().max(2000, "메모는 2000자를 넘을 수 없습니다.").optional(),
});

export type IssueRoundValues = z.infer<typeof issueRoundSchema>;

export const skipReasonSchema = z.object({
  reason: z.string().max(255, "사유는 255자를 넘을 수 없습니다.").optional(),
});

export type SkipReasonValues = z.infer<typeof skipReasonSchema>;

export const cancelReasonSchema = z.object({
  reason: z.string().max(255, "사유는 255자를 넘을 수 없습니다.").optional(),
});

export type CancelReasonValues = z.infer<typeof cancelReasonSchema>;

export const extendSchema = z.object({
  addRounds: z.number().int().positive("연장할 회차 수를 입력해 주세요."),
});

export type ExtendValues = z.infer<typeof extendSchema>;
